/**
 * @file A single error type for the third-party calls the browser makes, so
 * the UI can show something useful instead of a raw fetch failure.
 */

/** Which upstream service produced an error. */
export type ApiSource = 'places' | 'gemini';

/** An error from a user-key-authenticated Google API call. */
export class ApiError extends Error {
  readonly source: ApiSource;
  readonly status: number | undefined;

  constructor(source: ApiSource, message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.source = source;
    this.status = status;
  }

  /**
   * Whether the failure looks like a credential or permission problem, which
   * the UI answers by pointing the user at Settings rather than retrying.
   *
   * @returns True when the key is missing, invalid or lacks the API.
   */
  get isCredentialProblem(): boolean {
    return this.status === 400 || this.status === 401 || this.status === 403;
  }
}

/**
 * Turns an unknown thrown value into a presentable message.
 *
 * @param error Value caught from a failed call.
 * @returns A message safe to render.
 */
export function messageFor(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong.';
}
